import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import {
  Settings,
  Globe,
  MapPin,
  CheckCircle2,
  RotateCw,
  Smartphone,
  ChevronRight,
} from "lucide-react";

/* LocationHelpModal
 * -----------------
 * Step-by-step rescue guide for users whose browser permanently denied
 * geolocation. Detects platform on open and surfaces the right path:
 *   iOS Safari → Ajustes → Safari → Ubicación → Permitir
 *   Android Chrome → ⓘ icon in URL bar → Permisos → Ubicación → Permitir
 *   Desktop → site-settings padlock instructions
 *
 * The component is intentionally illustration-light (Lucide icons + numbered
 * steps + a pill-styled keyboard-cap that mimics the path the user will
 * actually walk through Settings). No screenshots required — that means it
 * stays accurate across iOS versions without re-shooting every release.
 */

const detectPlatform = () => {
  if (typeof window === "undefined") return "desktop";
  const ua = window.navigator.userAgent || "";
  const isIos =
    /iPad|iPhone|iPod/.test(ua) ||
    (window.navigator.platform === "MacIntel" && window.navigator.maxTouchPoints > 1);
  if (isIos) return "ios";
  if (/Android/.test(ua)) return "android";
  return "desktop";
};

const Crumb = ({ children, icon: Icon }) => (
  <span className="inline-flex items-center gap-1 rounded-md bg-sand px-2 py-0.5 text-[12px] font-medium text-agave ring-1 ring-black/5 whitespace-nowrap">
    {Icon ? <Icon className="w-3 h-3" /> : null}
    {children}
  </span>
);

const Arrow = () => <ChevronRight className="w-3 h-3 text-neutral-400 shrink-0" />;

const Step = ({ n, title, children }) => (
  <li className="flex gap-3" data-testid={`location-help-step-${n}`}>
    <span className="shrink-0 w-7 h-7 rounded-full bg-terracotta text-white text-sm font-semibold inline-flex items-center justify-center mt-0.5">
      {n}
    </span>
    <div className="min-w-0">
      <p className="font-medium text-[15px] text-agave leading-snug">{title}</p>
      {children ? (
        <div className="mt-1 text-sm text-neutral-600 leading-relaxed">{children}</div>
      ) : null}
    </div>
  </li>
);

export default function LocationHelpModal({ open, onOpenChange, onReload }) {
  // Platform is pure-derived from navigator; computing per-render is cheaper
  // than maintaining state + a useEffect dance, and avoids stale values when
  // the modal is reopened in a different context (e.g. after iOS handoff).
  const platform = open ? detectPlatform() : "ios";

  const title =
    platform === "ios"
      ? "Cómo activar la ubicación en iPhone"
      : platform === "android"
      ? "Cómo activar la ubicación en Android"
      : "Cómo activar la ubicación en tu navegador";

  const description =
    platform === "ios"
      ? "Safari recordó tu decisión anterior. Para usar “Buscar cerca de mí” reactívala desde Ajustes en 4 pasos."
      : platform === "android"
      ? "Chrome bloqueó la ubicación para Rivones. Reactívala desde los permisos del sitio."
      : "Tu navegador bloqueó la ubicación. Reactívala desde la configuración del sitio.";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // Force the modal above the Leaflet map controls (which can reach
        // z-index 700+). Without this override the iOS Safari user sees the
        // dialog flash and immediately disappear behind the map tiles, which
        // is exactly the "tap does nothing" symptom users reported.
        className="max-w-md z-[1100]"
        style={{ zIndex: 1100 }}
        data-testid="location-help-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-terracotta" />
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {platform === "ios" ? (
          <ol className="space-y-4 py-2">
            <Step n={1} title="Sal de Safari y abre Ajustes">
              <p>
                Busca el ícono gris con engranajes{" "}
                <Settings className="w-3.5 h-3.5 inline -mt-px" /> en tu pantalla
                de inicio.
              </p>
            </Step>
            <Step n={2} title="Toca Aplicaciones → Safari, o desliza hasta encontrarlo">
              <div className="flex items-center gap-1 flex-wrap mt-1">
                <Crumb icon={Settings}>Ajustes</Crumb>
                <Arrow />
                <Crumb>Aplicaciones</Crumb>
                <Arrow />
                <Crumb icon={Globe}>Safari</Crumb>
              </div>
              <p className="text-xs text-neutral-500 mt-1.5">
                En iOS 17 o anterior, Safari aparece directamente en la lista
                principal de Ajustes.
              </p>
            </Step>
            <Step n={3} title="Toca “Ubicación” y selecciona “Permitir”">
              <div className="flex items-center gap-1 flex-wrap mt-1">
                <Crumb icon={MapPin}>Ubicación</Crumb>
                <Arrow />
                <Crumb icon={CheckCircle2}>Permitir</Crumb>
              </div>
            </Step>
            <Step n={4} title="Regresa a Rivones y recarga">
              <p>Toca el botón de abajo para refrescar la página automáticamente.</p>
            </Step>
          </ol>
        ) : platform === "android" ? (
          <ol className="space-y-4 py-2">
            <Step n={1} title="Toca el ícono ⓘ o el candado en la barra de direcciones">
              <p>Aparece junto a la URL <strong>rivones.com</strong>.</p>
            </Step>
            <Step n={2} title="Abre Permisos → Ubicación">
              <div className="flex items-center gap-1 flex-wrap mt-1">
                <Crumb icon={Smartphone}>Permisos</Crumb>
                <Arrow />
                <Crumb icon={MapPin}>Ubicación</Crumb>
              </div>
            </Step>
            <Step n={3} title="Cambia a “Permitir”">
              <p>Si la opción no aparece, toca <strong>Restablecer permisos</strong> y vuelve a intentar.</p>
            </Step>
            <Step n={4} title="Recarga la página" />
          </ol>
        ) : (
          <ol className="space-y-4 py-2">
            <Step n={1} title="Haz clic en el candado a la izquierda de la URL" />
            <Step n={2} title="Activa “Ubicación” en Configuración del sitio">
              <div className="flex items-center gap-1 flex-wrap mt-1">
                <Crumb icon={Globe}>rivones.com</Crumb>
                <Arrow />
                <Crumb>Configuración del sitio</Crumb>
                <Arrow />
                <Crumb icon={MapPin}>Ubicación</Crumb>
              </div>
            </Step>
            <Step n={3} title="Recarga la página" />
          </ol>
        )}

        <div className="flex flex-col sm:flex-row gap-2 pt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="location-help-close-btn"
            className="sm:flex-1"
          >
            Más tarde
          </Button>
          <Button
            onClick={() => {
              onOpenChange(false);
              if (onReload) onReload();
              else window.location.reload();
            }}
            data-testid="location-help-reload-btn"
            className="sm:flex-1 bg-terracotta hover:bg-terracotta/90"
          >
            <RotateCw className="w-4 h-4 mr-2" />
            Ya activé, recargar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
